import React, { useEffect, useState } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';

const HeroSection = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Function to toggle mobile menu
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'auto';
  };

  // Function to handle smooth scrolling
  const scrollToSection = (sectionId, event) => {
    event.preventDefault();
    
    let section = null;
    
    // Find sections based on their unique characteristics
    if (sectionId === 'services-section') {
      // Find the Services section (first section with "Our Services" text)
      const elements = document.querySelectorAll('div.text-sm.text-neutral-600.mb-4');
      for (const el of elements) {
        if (el.textContent.trim() === 'Our Services') {
          section = el.closest('section');
          break;
        }
      }
    } 
    else if (sectionId === 'about-section') {
      // Find the How It Works section (section with "How Vedastik Works" heading)
      const elements = document.querySelectorAll('h2.text-6xl.font-light.mb-6');
      for (const el of elements) {
        if (el.textContent.includes('How Vedastik Works')) {
          section = el.closest('section');
          break;
        }
      }
    } 
    else if (sectionId === 'testimonials-section') {
      // Find Testimonials section (section with "Client Stories" text)
      const elements = document.querySelectorAll('div.inline-block.px-4.py-2.rounded-full');
      for (const el of elements) {
        if (el.textContent.includes('Client Stories')) {
          section = el.closest('section');
          break;
        }
      }
    }
    
    if (section) {
      // Close mobile menu if open
      if (isMobileMenuOpen) {
        toggleMobileMenu();
      }
      
      // Smooth scroll to the section
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Add useEffect to initialize AOS and handle any scroll-based animations
  useEffect(() => {
    // Initialize AOS
    AOS.init({
      duration: 1000,
      easing: 'ease-out-cubic',
      once: false,
      mirror: true,
      offset: 120
    });

    const handleScroll = () => {
      // Refresh AOS on scroll to trigger animations correctly
      AOS.refresh();
      
      // This could be used for additional scroll-based animations if needed
      const scrollY = window.scrollY;
      
      // Add scroll-based class changes for header (optional)
      const header = document.querySelector('header');
      if (header) {
        if (scrollY > 100) {
          header.style.backgroundColor = 'rgba(255, 255, 255, 0.98)';
          header.style.boxShadow = '0 4px 20px rgba(0,0,0,0.1)';
        } else {
          header.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
          header.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)';
        }
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Cleanup body overflow when component unmounts
    return () => {
      document.body.style.overflow = 'auto';
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="hero-section" style={{ 
      minHeight: '100vh', 
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: 'white'
    }}>
      {/* Modern Gradient Background */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 15% 30%, rgba(167, 215, 164, 0.07) 0%, rgba(255, 255, 255, 0) 40%), radial-gradient(circle at 85% 70%, rgba(167, 215, 164, 0.05) 0%, rgba(255, 255, 255, 0) 45%)',
          zIndex: 1
        }}
      />
      
      {/* Single Green Wave Pattern */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '50%', // Increased height for more wave visibility
          opacity: 1,
          zIndex: 2,
          pointerEvents: 'none',
          overflow: 'hidden'
        }}
      >
        <svg width="200%" height="100%" viewBox="0 0 2880 400" preserveAspectRatio="none">
          <defs>
            <linearGradient id="waveGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#A7D7A4" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#A7D7A4" stopOpacity="0.05" />
            </linearGradient>
            <linearGradient id="waveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#A7D7A4" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#A7D7A4" stopOpacity="0.02" />
            </linearGradient>
            <linearGradient id="waveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#A7D7A4" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#A7D7A4" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          
          {/* Wave 1 - Bottom layer */}
          <path 
            d="M0,150 
               C240,200 480,100 720,150 
               C960,200 1200,100 1440,150 
               C1680,200 1920,100 2160,150 
               C2400,200 2640,100 2880,150 
               L2880,400 L0,400 Z" 
            fill="url(#waveGradient1)" 
            className="wave wave-1"
          />
          
          {/* Wave 2 - Middle layer */}
          <path 
            d="M0,120 
               C240,80 480,160 720,120 
               C960,80 1200,160 1440,120 
               C1680,80 1920,160 2160,120 
               C2400,80 2640,160 2880,120 
               L2880,400 L0,400 Z" 
            fill="url(#waveGradient2)" 
            className="wave wave-2"
          />
          
          {/* Wave 3 - Top layer */}
          <path 
            d="M0,100 
               C180,140 360,60 540,100 
               C720,140 900,60 1080,100 
               C1260,140 1440,60 1620,100 
               C1800,140 1980,60 2160,100 
               C2340,140 2520,60 2700,100
               C2780,120 2840,90 2880,100
               L2880,400 L0,400 Z" 
            fill="url(#waveGradient3)" 
            className="wave wave-3"
          />
          
          {/* Wave Outline for Definition */}
          <path 
            d="M0,100 
               C180,140 360,60 540,100 
               C720,140 900,60 1080,100 
               C1260,140 1440,60 1620,100 
               C1800,140 1980,60 2160,100 
               C2340,140 2520,60 2700,100
               C2780,120 2840,90 2880,100" 
            fill="none" 
            stroke="#A7D7A4" 
            strokeWidth="1.5"
            opacity="0.6"
            className="wave wave-3"
          />
        </svg>
      </div>
      
      {/* CSS Animations */}
      <style dangerouslySetInnerHTML={{ __html: `
        /* Wave animations with different speeds and directions */
        .wave-1 {
          animation: waveMovement1 20s linear infinite;
        }
        
        .wave-2 {
          animation: waveMovement2 15s linear infinite;
        }
        
        .wave-3 {
          animation: waveMovement3 12s linear infinite;
        }
        
        @keyframes waveMovement1 {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        
        @keyframes waveMovement2 {
          from {
            transform: translateX(-50%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        @keyframes waveMovement3 {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-50%);
          }
        }
        
        /* Navbar styling */
        .nav-link {
          position: relative;
        }
        
        .nav-link:hover {
          color: #305248;
          opacity: 0.8;
        }
        
        .nav-link::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: -5px;
          left: 0;
          background-color: #305248;
          transition: width 0.3s ease;
        }
        
        .nav-link:hover::after {
          width: 100%;
        }
        
        .book-call-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(48, 82, 72, 0.3);
        }
        
        /* Button hover animations */
        .primary-button {
          transition: all 0.3s ease;
          transform: translateY(0);
          box-shadow: 0 4px 10px rgba(48, 82, 72, 0.2);
          position: relative;
          overflow: hidden;
        }
        
        .primary-button:before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: 0.5s;
        }
        
        .primary-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 15px rgba(48, 82, 72, 0.25);
        }
        
        .primary-button:hover:before {
          left: 100%;
        }
        
        .secondary-button {
          transition: all 0.3s ease;
          transform: translateY(0);
          border: 1px solid rgba(48, 82, 72, 0.3);
          position: relative;
          overflow: hidden;
        }
        
        .secondary-button:before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(48, 82, 72, 0.1), transparent);
          transition: 0.5s;
        }
        
        .secondary-button:hover {
          transform: translateY(-2px);
          background-color: rgba(48, 82, 72, 0.05);
          border: 1px solid rgba(48, 82, 72, 0.4);
        }
        
        .secondary-button:hover:before {
          left: 100%;
        }
        
        /* Responsive adjustments */
        @media (max-width: 1024px) {
          .desktop-nav {
            position: relative;
            left: auto;
            transform: none;
            margin: 0 auto 0 0;
            padding-left: 40px;
            gap: 25px;
          }
        }
        
        @media (max-width: 850px) {
          .desktop-nav {
            display: none !important;
          }
          
          .mobile-menu-btn {
            display: block !important;
          }
        }
        
        @media (max-width: 768px) {
          header {
            padding: 15px 20px !important;
          }
          
          .book-call-btn {
            display: none !important;
          }
          
          .hero-stats {
            flex-direction: column;
            gap: 24px !important;
          }
          
          .hero-buttons {
            flex-direction: column;
            width: 100%;
          }
          
          .hero-title {
            font-size: 42px !important;
            margin-top: 15px !important;
          }
          
          .hero-description {
            font-size: 18px !important;
          }
          
          /* Adjust badge spacing on mobile */
          .hero-section .main-content {
            padding-top: 60px !important;
          }
        }
      `}} />
      
      {/* Main Container */}
      <div style={{
        position: 'relative',
        minHeight: '100vh',
        zIndex: 5
      }}>
        {/* Header */}
        <header style={{ 
          width: '100%', 
          padding: '15px 5%', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 100,
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          transition: 'all 0.3s ease'
        }}>
          <div 
            data-aos="fade-right"
            data-aos-delay="100"
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/vedastik.svg" alt="Vedastik Logo" style={{ height: '30px' }} />
            <span style={{ color: '#305248', fontSize: '22px', fontWeight: '600', letterSpacing: '0.5px' }}>Vedastik</span>
          </div>
          
          {/* Desktop Nav Links - HIDDEN on mobile */}
          <nav className="desktop-nav" style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '40px',
            margin: '0 auto',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)'
          }}>
            <a 
              data-aos="fade-down" 
              data-aos-delay="150"
              href="#services-section" 
              onClick={(e) => scrollToSection('services-section', e)}
              style={{ color: '#305248', fontSize: '16px', textDecoration: 'none', fontWeight: '500', transition: 'all 0.2s ease', position: 'relative' }} 
              className="nav-link"
            >
              Longevity Programs
            </a>
            <a 
              data-aos="fade-down" 
              data-aos-delay="200"
              href="#about-section" 
              onClick={(e) => scrollToSection('about-section', e)}
              style={{ color: '#305248', fontSize: '16px', textDecoration: 'none', fontWeight: '500', transition: 'all 0.2s ease', position: 'relative' }} 
              className="nav-link"
            >
              Our Science
            </a>
            <a 
              data-aos="fade-down" 
              data-aos-delay="250"
              href="#testimonials-section" 
              onClick={(e) => scrollToSection('testimonials-section', e)}
              style={{ color: '#305248', fontSize: '16px', textDecoration: 'none', fontWeight: '500', transition: 'all 0.2s ease', position: 'relative' }} 
              className="nav-link"
            >
              Success Stories
            </a>
          </nav>
          
          {/* Book Call Button - HIDDEN on mobile */}
          <a 
            data-aos="fade-left"
            data-aos-delay="300"
            href="https://calendly.com/jaganmohan97" 
            target="_blank"
            rel="noopener noreferrer"
            className="book-call-btn"
            style={{
              backgroundColor: '#305248',
              color: 'white',
              padding: '10px 20px',
              borderRadius: '8px',
              fontSize: '15px',
              fontWeight: '500',
              textDecoration: 'none',
              display: 'inline-block',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(48, 82, 72, 0.25)'
            }}
          >
            Book a Call
          </a>
          
          {/* Mobile Menu Button */}
          <button 
            className="mobile-menu-btn"
            onClick={toggleMobileMenu}
            style={{
              display: 'none',
              background: 'transparent',
              border: 'none',
              color: '#305248',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '5px',
              zIndex: 102
            }}
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </button>
          
          {/* Mobile Menu Overlay */}
          <div 
            className="mobile-menu"
            style={{
              display: isMobileMenuOpen ? 'flex' : 'none',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'white',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 101,
              padding: '20px'
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', alignItems: 'center' }}>
              <a 
                href="#services-section" 
                onClick={(e) => scrollToSection('services-section', e)}
                style={{ color: '#305248', fontSize: '24px', textDecoration: 'none', fontWeight: '500' }}
              >
                Longevity Programs
              </a>
              <a 
                href="#about-section" 
                onClick={(e) => scrollToSection('about-section', e)}
                style={{ color: '#305248', fontSize: '24px', textDecoration: 'none', fontWeight: '500' }}
              >
                Our Science
              </a>
              <a 
                href="#testimonials-section" 
                onClick={(e) => scrollToSection('testimonials-section', e)}
                style={{ color: '#305248', fontSize: '24px', textDecoration: 'none', fontWeight: '500' }}
              >
                Success Stories
              </a>
              <a 
                href="https://calendly.com/jaganmohan97" 
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  backgroundColor: '#305248',
                  color: 'white',
                  padding: '14px 28px',
                  borderRadius: '8px',
                  fontSize: '18px',
                  fontWeight: '500',
                  textDecoration: 'none',
                  marginTop: '15px',
                  boxShadow: '0 4px 10px rgba(48, 82, 72, 0.2)'
                }}
                onClick={toggleMobileMenu}
              >
                Book a Call
              </a>
            </div>
          </div>
        </header>
        
        {/* Main Content */}
        <div 
          className="main-content"
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 'calc(100vh - 70px)',
            padding: '80px 24px 40px',
            textAlign: 'center',
            position: 'relative'
          }}>
          <main style={{ 
            maxWidth: '800px', 
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            position: 'relative',
            zIndex: 10
          }}>
            <span 
              data-aos="fade-down"
              data-aos-delay="100"
              style={{ 
                backgroundColor: '#305248', 
                padding: '8px 18px', 
                borderRadius: '9999px', 
                color: 'white', 
                fontSize: '14px', 
                marginBottom: '28px',
                marginTop: '80px',
                letterSpacing: '0.5px',
                transform: 'translateY(0)',
                transition: 'transform 0.3s ease',
                display: 'inline-block'
              }}
              className="hover:transform hover:scale-105">
              Advanced Longevity Solutions
            </span>
            
            <h1 
              data-aos="fade-up"
              data-aos-delay="200"
              className="hero-title" 
              style={{ 
                color: '#305248', 
                fontSize: '56px', 
                fontWeight: '300', 
                lineHeight: '1.2', 
                marginBottom: '28px',
                marginTop: '20px',
                letterSpacing: '0.5px'
              }}>
              Vedastik: Pioneering <br />Your Longevity Journey
            </h1>
            
            <p 
              data-aos="fade-up"
              data-aos-delay="300"
              className="hero-description" 
              style={{ 
                color: '#4a4a4a', 
                fontSize: '20px', 
                lineHeight: '1.6',
                marginBottom: '48px', 
                maxWidth: '700px',
                fontWeight: '300'
              }}>
              Unlocking the science of extended human lifespan through personalized 
              longevity protocols that blend ancient wisdom with cutting-edge biotechnology.
            </p>
            
            <div 
              data-aos="fade-up"
              data-aos-delay="400"
              className="hero-buttons" 
              style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '16px', 
                marginBottom: '60px', 
                width: '100%',
                alignItems: 'center'
              }}>
              {/* Primary Button with hover effect */}
              <a 
                href="https://calendly.com/jaganmohan97"
                target="_blank"
                rel="noopener noreferrer"
                className="primary-button"
                style={{
                  backgroundColor: '#305248',
                  color: 'white',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '400',
                  textAlign: 'center',
                  textDecoration: 'none',
                  boxShadow: '0 4px 10px rgba(48, 82, 72, 0.2)',
                  border: 'none',
                  width: 'auto',
                  minWidth: '180px',
                  display: 'inline-block'
                }}
              >
                Begin Your Longevity Journey
              </a>
              
              {/* Secondary Button with hover effect */}
              <a 
                href="#about-section"
                onClick={(e) => scrollToSection('about-section', e)}
                className="secondary-button"
                style={{
                  backgroundColor: 'transparent',
                  color: '#305248',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '400',
                  textAlign: 'center',
                  textDecoration: 'none',
                  border: '1px solid rgba(48, 82, 72, 0.3)',
                  width: 'auto',
                  minWidth: '180px',
                  display: 'inline-block'
                }}
              >
                Explore Vedastik's Approach
              </a>
            </div>
            
            {/* Stats Section with enhanced styling */}
            <div 
              data-aos="fade-up"
              data-aos-delay="500"
              className="hero-stats" 
              style={{ 
                width: '100%', 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '80px', 
                margin: '0 auto 60px'
              }}>
              <div data-aos="zoom-in" data-aos-delay="600" style={{ textAlign: 'center', color: '#305248' }} className="hover:transform hover:scale-105 transition-transform duration-300">
                <div style={{ fontSize: '36px', fontWeight: '300', marginBottom: '8px' }}>15+</div>
                <div style={{ fontSize: '15px', opacity: '0.7', fontWeight: '400', letterSpacing: '0.5px' }}>Longevity Protocols</div>
              </div>
              <div data-aos="zoom-in" data-aos-delay="700" style={{ textAlign: 'center', color: '#305248' }} className="hover:transform hover:scale-105 transition-transform duration-300">
                <div style={{ fontSize: '36px', fontWeight: '300', marginBottom: '8px' }}>98%</div>
                <div style={{ fontSize: '15px', opacity: '0.7', fontWeight: '400', letterSpacing: '0.5px' }}>Client Health Improvement</div>
              </div>
              <div data-aos="zoom-in" data-aos-delay="800" style={{ textAlign: 'center', color: '#305248' }} className="hover:transform hover:scale-105 transition-transform duration-300">
                <div style={{ fontSize: '36px', fontWeight: '300', marginBottom: '8px' }}>20+</div>
                <div style={{ fontSize: '15px', opacity: '0.7', fontWeight: '400', letterSpacing: '0.5px' }}>Biomarkers Analyzed</div>
              </div>
            </div>
          </main>
          
          {/* Scroll Indicator with enhanced animation */}
          <div 
            data-aos="fade-up"
            data-aos-delay="900"
            style={{ 
              marginTop: '10px',
              marginBottom: '30px', 
              textAlign: 'center', 
              color: '#305248', 
              fontSize: '14px',
              opacity: '0.7',
              position: 'relative',
              zIndex: 10
            }}>
            <span style={{ display: 'block', marginBottom: '10px', letterSpacing: '0.5px' }}>Scroll to explore</span>
            <svg width="16" height="24" viewBox="0 0 16 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto' }}>
              <rect x="1" y="1" width="14" height="22" rx="7" stroke="currentColor" strokeWidth="1" strokeOpacity="0.5" className="animate-pulse"/>
              <circle cx="8" cy="7" r="3" fill="currentColor" fillOpacity="0.5">
                <animate
                  attributeName="cy"
                  values="7;16;7"
                  dur="1.5s"
                  repeatCount="indefinite"
                  calcMode="ease-in-out"
                />
                <animate
                  attributeName="opacity"
                  values="0.5;0.8;0.5"
                  dur="1.5s"
                  repeatCount="indefinite"
                  calcMode="ease-in-out"
                />
              </circle>
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroSection; 